import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {Scrollbars} from 'react-custom-scrollbars';

import {
    Editor,
    ContentState,
    convertFromHTML,
    RichUtils,
    EditorState,
    Modifier,
    SelectionState,
    getDefaultKeyBinding,
    KeyBindingUtil,
} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import classnames from 'classnames';
import styles from './RichEditor.scss';
import InlineToolbar from './InlineToolbar';
import MacroList from 'components/controls/MacroList';
import {useMacros} from 'components/helpers/hooks/useMacros';
import useDebounce from 'components/helpers/hooks/useDebounce';

const LIST_TOP_OFFSET = 70;
const LIST_LEFT_OFFSET = 5;
const MACRO_USE_COMMAND = 'macro-use';
const SUBMIT_COMMAND = 'submit-command';
const ENTER_KEY_CODE = 13;

export const RICH_EDITOR_VALUE = Symbol('rich-editor-value');

function getValueFromEditorState(editorState) {
    return stateToHTML(editorState.getCurrentContent());
}

function getStateFromString(value) {
    const blocksFromHTML = (value && convertFromHTML(value));
    return EditorState.moveFocusToEnd(
        blocksFromHTML
            ? EditorState.createWithContent(ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap,
            ))
            : EditorState.createEmpty()
    );
}

export default function NewRichEditor(props) {
    const {
        value,
        onSubmit,
        onChange,
        dropdownList,
        dropdownListProcessing,
        className,
        placeholder,
        withMacro,
        initialHeight,
        disabled,
        onSearch,
        patientId,
        autoFocus,
    } = props;
    const [isSearching, setSearching] = useState(false);
    const handleSearch = useCallback((...args) => {
        onSearch(...args);
        setSearching(false);
    }, [onSearch]);

    const editorState = useMemo(() => value.editorState
        ? value.editorState
        : getStateFromString(value),
        [value],
    );
    const editorRef = useRef(null);

    const [
        {
            macro,
            macroDropDownPosition,
        }, ,
        containerRef,
    ] = useMacros(editorState);

    const [{placeholderHidden}, setState] = useState({
        placeholderHidden: false,
    });

    const handleChange = (newState) => {
        const value = getValueFromEditorState(newState);
        onChange({
            target: {
                value: {
                    editorState: newState,
                    value,
                    [RICH_EDITOR_VALUE]: true,
                },
            },
        });
    };

    const toggleInlineStyle = inlineStyle => {
        handleChange(
            RichUtils.toggleInlineStyle(
                value.editorState,
                inlineStyle
            )
        );
    };

    const toggleBlockType = blockStyle => {
        handleChange(
            RichUtils.toggleBlockType(
                value.editorState,
                blockStyle
            )
        );
    };

    const handleKeyCommand = (command, editorState) => {
        if ([SUBMIT_COMMAND, MACRO_USE_COMMAND].includes(command)) {
            return 'handled';
        }
        return 'not-handled';
    };

    const handleSelectMacro = selectedMacro => {
        const content = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const block = content.getBlockForKey(selection.getAnchorKey());
        const start = block.getText().indexOf('#');
        const selectionEnd = selection.getEndOffset();
        const newContent = Modifier.replaceText(
            editorState.getCurrentContent(),
            new SelectionState({
                anchorKey: block.getKey(),
                anchorOffset: start,
                focusKey: block.getKey(),
                focusOffset: selectionEnd,
            }),
            selectedMacro.text
        );
        const newEditorState = EditorState.push(
            editorState,
            newContent,
            'replace-text'
        );
        handleChange(EditorState.moveFocusToEnd(newEditorState));
    };

    useEffect(() => {
        if (typeof value === 'string') {
            const newState = getStateFromString(value);
            onChange({
                target: {
                    value: {
                        editorState: newState,
                        value,
                        [RICH_EDITOR_VALUE]: true,
                    },
                },
            });
        }
    }, [value]);

    const debouncedSearch = useDebounce(
        handleSearch,
        500,
    );

    useEffect(() => {
        if (macro && onSearch && withMacro) {
            setSearching(true);
            debouncedSearch(macro, patientId);
        }
    }, [macro])

    const keyBindingFn = (e) => {
        if (e.keyCode === ENTER_KEY_CODE) {
            if (macro) {
                return MACRO_USE_COMMAND;
            }

            if (KeyBindingUtil.hasCommandModifier(e) && !macro) {
                onSubmit && onSubmit();

                return SUBMIT_COMMAND;
            }
        }

        return getDefaultKeyBinding(e);
    };

    const handleClickContainer = () => {
        editorRef.current.focus();
    };

    useEffect(() => {
        if (autoFocus) {
            setImmediate(editorRef.current.focus);
        }
    }, [autoFocus]);


    const listVisible = Boolean(macro)
        && Boolean(dropdownList && dropdownList.length)
        && !dropdownListProcessing
        && macroDropDownPosition
        && macroDropDownPosition.top
        && macroDropDownPosition.left
        && !isSearching;

    return (
        <div
            className={classnames(styles.container, className, {
                [styles.disabled]: disabled,
                [styles.placeholderHidden]: placeholderHidden,
            })}
            onClick={handleClickContainer}
            ref={containerRef}
        >

            <Scrollbars autoHeightMax={initialHeight} autoHide autoHeight className={styles.scrollbar}>
                <Editor
                    editorState={editorState}
                    handleKeyCommand={handleKeyCommand}
                    onChange={handleChange}
                    placeholder={placeholder}
                    readOnly={disabled}
                    keyBindingFn={keyBindingFn}
                    ref={editorRef}
                />
            </Scrollbars>
            {listVisible &&
                <MacroList
                    parent={containerRef}
                    list={dropdownList}
                    processing={dropdownListProcessing}
                    onSelect={handleSelectMacro}
                    visible={listVisible}
                    macroDropDownPosition={{
                        top: macroDropDownPosition ? macroDropDownPosition.top + LIST_TOP_OFFSET : 0,
                        left: macroDropDownPosition ? macroDropDownPosition.left + LIST_LEFT_OFFSET : 0,
                    }}
                />}
            <InlineToolbar
                className={classnames(styles.toolbar)}
                editorState={editorState}
                onToggleInline={toggleInlineStyle}
                onToggleBlock={toggleBlockType}
            />
        </div>
    );
}

NewRichEditor.propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string,]),
    height: PropTypes.number,
    dropdownList: PropTypes.array,
    dropdownListProcessing: PropTypes.bool,
    disabled: PropTypes.bool,
};

NewRichEditor.defaultProps = {
    value: '',
    height: 0,
    dropdownList: [],
};



// WEBPACK FOOTER //
// ./src/components/controls/RichEditor/NewRichEditor.jsx